import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Lock, Calendar, AlertCircle, Shield, CreditCard, Tag, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import PageSeo from '@/components/PageSeo';

export default function LockFlightPriceWithDeposit() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canonicalUrl = 'https://defera.app/guides/lock-flight-price-with-deposit';
  const metaTitle = 'How to Lock in Flight Prices With a Deposit (Fare Hold vs Price Freeze vs Layaway)';
  const metaDescription = 'Learn the safest ways to lock airfare: deposits, fare holds, price freeze tools, and layaway-style payment plans—plus checklists to avoid hidden fees and surprises.';

  const quickMethods = [
    {
      method: 'Airline fare hold',
      whatItDoes: 'Holds the price for a short window (e.g., United FareLock, for a fee)',
      bestFor: 'Finalizing dates, visas, or approvals',
      watchOut: 'Short timelines; may have a fee'
    },
    {
      method: 'Price Freeze tools',
      whatItDoes: 'Lets you pay to lock a price temporarily',
      bestFor: 'Waiting for payday while avoiding price jumps',
      watchOut: 'Fewer sites offer them now; freeze fee may be non-refundable'
    },
    {
      method: 'Deposit-based layaway',
      whatItDoes: 'Reserve itinerary with a deposit and pay over time',
      bestFor: 'Planning ahead with predictable installments',
      watchOut: 'Confirm when you get the confirmation number, and the change rules'
    },
    {
      method: 'Installment checkout (BNPL)',
      whatItDoes: 'Book now; a lender pays and you repay in installments',
      bestFor: 'Booking immediately with a fixed schedule',
      watchOut: 'Late fees/APR possible on longer terms'
    }
  ];

  const depositSteps = [
    {
      title: 'Choose the itinerary you want',
      detail: 'Pick the exact dates, routing, and cabin you would book today.'
    },
    {
      title: 'Confirm the lock mechanism',
      detail: 'Verify if it is a fare hold, a deposit plan, or a price freeze—and how long it lasts.'
    },
    {
      title: 'Review the total cost and policies',
      detail: 'Check service fees, refund rules, and change/cancellation terms before paying anything.'
    },
    {
      title: 'Pay the deposit (or freeze fee)',
      detail: 'Pay the required upfront amount and save confirmation details immediately.'
    },
    {
      title: 'Set your payment schedule and reminders',
      detail: 'Turn on autopay if available and align due dates to your pay cycle.'
    },
    {
      title: 'Track your confirmation and deadlines',
      detail: 'Know whether the booking is confirmed right away or after the final payment, then plan changes accordingly.'
    }
  ];

  const feeChecklist = [
    'Is the total cost (including fees) shown before you confirm?',
    'Does the plan charge interest/APR or only fixed fees?',
    'Are there late fees, and how are they triggered?',
    'When do you get the airline confirmation number (right away vs after final payment)?',
    'Can you change dates, and what are the penalties?',
    'Does the provider offer support if your plans change?'
  ];

  const faqs = [
    {
      question: 'Is a deposit the same as a fare hold?',
      answer: 'Not always. A fare hold usually reserves the price for a limited time. A deposit plan may reserve the itinerary and spread the remaining balance over multiple payments. Always confirm the timeline and when the booking is confirmed.'
    },
    {
      question: 'When is the booking confirmed on deposit or layaway plans?',
      answer: 'It depends on the provider. Some confirm the booking right after a deposit; others confirm it after the final payment. With Defera, your trip price is secured once your booking is approved, and your airline confirmation number arrives once the plan is paid in full. Check this before committing so you understand your change and refund options.'
    },
    {
      question: 'Can prices still change after I pay a deposit?',
      answer: 'Some services advertise price protection during the payment window, but policies vary. Read the terms carefully and keep proof of your locked price and dates.'
    },
    {
      question: 'What is the safest way to avoid hidden fees?',
      answer: 'Use providers that show a full schedule, the total cost, and policies upfront. Prefer autopay and avoid choosing a plan that stretches your budget too thin.'
    },
    {
      question: 'How does Defera help you lock in a trip?',
      answer: 'With Defera Installments you pay a deposit (as low as 5% on trips 90+ days out; 20% at 60–89 days, 40% at 30–59 and 60% at 22–29), then automatic weekly or bi-weekly payments, with no credit check and no interest. Once your booking is approved, your agreed-upon trip price is secured and protected from future fare increases while you stay in good standing. Defera Zero is $0 down, on refundable fares from American, United, Southwest, JetBlue and Virgin Atlantic.'
    }
  ];

  return (
    <>
      <PageSeo
        path="/guides/lock-flight-price-with-deposit"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: metaTitle,
            description: metaDescription,
            author: { '@type': 'Organization', name: 'Defera' },
            publisher: {
              '@type': 'Organization',
              name: 'Defera',
              logo: {
                '@type': 'ImageObject',
                url: 'https://defera.app/defera-transparent.png'
              }
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'How to lock in a flight price with a deposit',
            description: 'Step-by-step checklist for using deposits, fare holds, or layaway to lock airfare while avoiding surprises.',
            step: depositSteps.map((s, i) => ({
              '@type': 'HowToStep',
              position: i + 1,
              name: s.title,
              text: s.detail
            }))
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer }
            }))
          },
        ]}
      />

      <div className="min-h-screen bg-black text-white">
        {/* Navigation Bar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/[0.08]"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link
              to="/guides"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Guides
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/how-it-works')}
                className="text-white/30 hover:text-white/60 text-sm font-normal transition-colors"
              >
                How it Works
              </button>
              <button
                onClick={() => navigate('/search')}
                className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Search Flights
              </button>
            </div>
          </div>
        </motion.div>

        {/* Hero */}
        <section className="pt-28 pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-white/45 text-xs font-normal uppercase tracking-wider">
                  Price Lock Guide
                </span>
                <span className="text-white/40 text-xs">Updated November 2026</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                How to Lock in Flight Prices With a Deposit
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed">
                Flight prices move fast. If you found a fare that works, you can sometimes lock it using a deposit, a fare hold, or a price-freeze tool.
                This guide explains the main methods, what they protect, and what to double-check so you do not get surprised by fees or confirmation rules.
              </p>
            </motion.div>
          </div>
        </section>

        {/* AEO quick answer */}
        <section className="pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-[#1d1d1f] border border-white/[0.08] rounded-[1.25rem] p-6 md:p-8"
            >
              <h2 className="text-xl md:text-2xl font-bold text-white mb-3">Quick answer</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">
                    The safest way to lock a fare is to <strong className="text-white">confirm when the booking is confirmed</strong> and the <strong className="text-white">total cost</strong> before paying a deposit.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">
                    Use <strong className="text-white">fare holds</strong> for short windows, <strong className="text-white">price freeze</strong> if you need a few extra days, and <strong className="text-white">deposit plans</strong> if you are paying over weeks.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">
                    Always check for <strong className="text-white">service fees</strong>, <strong className="text-white">refund rules</strong>, and <strong className="text-white">late-payment consequences</strong>.
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Methods table */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Four common ways to lock airfare</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-4 text-white font-semibold">Method</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">What it does</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Best for</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Watch out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quickMethods.map((row, idx) => (
                      <tr key={idx} className="border-b border-white/10">
                        <td className="py-3 px-4 text-white/90 font-medium">{row.method}</td>
                        <td className="py-3 px-4 text-white/80">{row.whatItDoes}</td>
                        <td className="py-3 px-4 text-white/80">{row.bestFor}</td>
                        <td className="py-3 px-4 text-white/80">{row.watchOut}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>

        {/* HowTo steps */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Step-by-step: lock a price with a deposit</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {depositSteps.map((step, index) => (
                  <div key={index} className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-white/[0.08] text-white/90 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">{step.title}</h3>
                        <p className="text-white/80 text-sm leading-relaxed">{step.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5 flex items-start gap-4">
                <AlertCircle className="w-5 h-5 text-white/90 flex-shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm">
                  <strong className="text-white/90">Tip:</strong> If you are paying over time, booking earlier generally gives you more payment runway and often a smaller deposit.
                  On Defera, trips must depart at least 22 days out, and deposits start as low as 5% for trips 90+ days away (20% at 60–89 days, 40% at 30–59, 60% at 22–29).
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Fee checklist */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Avoid surprises: fee + confirmation checklist</h2>
              <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-2xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {feeChecklist.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Defera CTA */}
        <section className="py-14 px-6 bg-zinc-950/50 border-t border-white/[0.06]">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-white/70" />
                <h2 className="text-2xl md:text-3xl font-bold text-white">Lock plans with clarity</h2>
              </div>
              <p className="text-white/80 leading-relaxed mb-6">
                If your priority is predictable payments and transparent terms, Defera is built to help you plan confidently. There’s no credit check and no interest,
                and once your booking is approved your agreed-upon trip price is secured. Choose weekly or bi-weekly payments and manage them in one place.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => navigate('/search')} className="bg-white hover:bg-white/90 text-black px-6 py-3 rounded-full font-semibold text-sm">
                  Search Flights
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/guides/flight-layaway-vs-bnpl')}
                  className="border border-white/15 text-white hover:bg-white/10 px-6 py-3 rounded-full font-semibold text-sm"
                >
                  Layaway vs BNPL
                  <CreditCard className="w-4 h-4 ml-2" />
                </Button>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Tag className="w-5 h-5 text-white/55" />
                    <h3 className="text-white font-semibold">Upfront pricing</h3>
                  </div>
                  <p className="text-white/80 text-sm">Know the schedule, total cost, and policies before you commit.</p>
                </div>
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="w-5 h-5 text-white/70" />
                    <h3 className="text-white font-semibold">Budget-friendly cadence</h3>
                  </div>
                  <p className="text-white/80 text-sm">Align payments to your paycheck cycle for smoother cash flow.</p>
                </div>
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Timer className="w-5 h-5 text-white/90" />
                    <h3 className="text-white font-semibold">Deadline awareness</h3>
                  </div>
                  <p className="text-white/80 text-sm">Know your payment dates and when your airline confirmation number arrives (once the plan is paid in full).</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl px-6 py-5 hover:border-white/20 transition-colors">
                      <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                      <p className="text-white/80 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Related guides</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/guides/airline-fare-hold-policies" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Airline fare hold policies
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/flight-layaway-vs-bnpl" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Flight layaway vs BNPL: what’s the difference?
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/when-to-book-flights-fare-data" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  When to book flights: what fare data shows
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/flight-installments-bnpl-payment-options" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Book flights on installments without a credit card
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
            </ul>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
